import { useCallback, useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconNote } from '@tabler/icons-react';
import { CrudMode } from '@ohpen/enso-ui';
import { useAuth } from '@ohpen/react-auth';
import { Button, Flex } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form/hooks/useForm';

import { NoteResponse, ActionEntityType } from '@pmp/api';
import { usePmpFetch } from '@pmp/hooks';
import { useValueListsStore } from '@pmp/stores';

import { TabContent } from '../../firms/components/TabContent';
import { CrudFormCard } from '../../firms/components/CrudFormCard';
import firmStyles from '../../firms/styles.module.css';
import { NotesCrudForm } from './NotesCrudForm';
import { NotesList } from './NotesList';
import { NotesDeleteModal } from './NotesDeleteModal';
import { getNotesInitialState, notesReducer } from '../reducer';
import { NOTES_LIST_NAMES } from '../utils';

export type NotesTabProps = {
  form: FormInstance<any> | undefined;
  entityType?: ActionEntityType;
  entityIdField: string;
  entityId?: string;
};

export const NotesTab: React.FC<NotesTabProps> = ({ form, entityType, entityIdField, entityId: entityIdProp }) => {
  const [, , data, , fetchData] = usePmpFetch('notesGetNotes');
  const { valueLists, getValueLists } = useValueListsStore();
  const [state, dispatch] = useReducer(notesReducer<NoteResponse>, getNotesInitialState<NoteResponse>());
  const { t } = useTranslation();
  const { userName } = useAuth();

  const entityId = entityIdProp ?? (form?.getFieldValue?.(entityIdField) as string);

  useEffect(() => {
    getValueLists({ alwaysFetch: NOTES_LIST_NAMES });
  }, [getValueLists]);

  const refreshTable = useCallback(() => {
    fetchData({ query: { entityType, entityId } });
  }, [fetchData, entityType, entityId]);

  useEffect(() => {
    refreshTable();
  }, [refreshTable]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onEdit = useCallback((record: NoteResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: NoteResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <TabContent noScroll>
      <Flex className={firmStyles.notesContainer} justify="space-between">
        <CrudFormCard
          icon={<IconNote />}
          title={t('global.labels.notes')}
          extra={
            <Flex justify="end">
              <Button icon={<PlusOutlined />} onClick={onCreate} size="small">
                {t('global.buttons.addNote')}
              </Button>
            </Flex>
          }
        >
          <NotesList
            notes={data ?? []}
            valueLists={valueLists}
            onCreate={onCreate}
            onEdit={onEdit}
            onDelete={onDelete}
            userName={userName}
          />
        </CrudFormCard>
      </Flex>
      {state.crud.open && state.crud.mode && (
        <NotesCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          valueLists={valueLists}
          refreshTable={refreshTable}
          onClose={onCloseCrud}
          entityType={entityType}
          entityId={entityId}
          userName={userName}
        />
      )}
      {state.deleteModal.open && (
        <NotesDeleteModal
          open={state.deleteModal.open}
          record={state.deleteModal.record}
          onClose={onCloseDeleteModal}
          refreshTable={refreshTable}
          entityType={entityType}
        />
      )}
    </TabContent>
  );
};
